import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const emptyCart = { items: [], subtotal_mrp: 0, subtotal_price: 0, total_savings: 0, total: 0, count: 0 };

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState(emptyCart);
  const [loading, setLoading] = useState(false);

  const isCustomer = user && user !== false && user.role !== "admin";

  const fetchCart = useCallback(async () => {
    if (!isCustomer) {
      setCart(emptyCart);
      return;
    }
    try {
      const { data } = await api.get("/cart");
      setCart(data);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
      setCart(emptyCart);
    }
  }, [isCustomer]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = useCallback(async (productId, quantity = 1) => {
    if (!isCustomer) {
      throw new Error("login-required");
    }
    setLoading(true);
    try {
      const { data } = await api.post("/cart/items", { product_id: productId, quantity });
      setCart(data);
      toast.success("Added to cart");
    } finally {
      setLoading(false);
    }
  }, [isCustomer]);

  const updateQty = useCallback(async (productId, quantity) => {
    const { data } = await api.put(`/cart/items/${productId}`, { product_id: productId, quantity });
    setCart(data);
  }, []);

  const removeItem = useCallback(async (productId) => {
    const { data } = await api.delete(`/cart/items/${productId}`);
    setCart(data);
    toast.success("Removed from cart");
  }, []);

  const clearCart = useCallback(async () => {
    const { data } = await api.delete("/cart");
    setCart(data);
  }, []);

  const value = useMemo(
    () => ({ cart, loading, fetchCart, addToCart, updateQty, removeItem, clearCart }),
    [cart, loading, fetchCart, addToCart, updateQty, removeItem, clearCart]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
